import React, { useState, useEffect } from 'react';
import { Save, Plus, X, RotateCcw, Shield, MessageSquarePlus, ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import { API_URL } from '../../config';

export default function TriggerWordsAdmin() {
  const [triggerWords, setTriggerWords] = useState([]);
  const [promptKeywords, setPromptKeywords] = useState([]);
  const [newWord, setNewWord] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [remixPrompt, setRemixPrompt] = useState('');
  const [savingRemix, setSavingRemix] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [remixLog, setRemixLog] = useState([]);
  const [remixLogOpen, setRemixLogOpen] = useState(false);
  const [remixLogLoading, setRemixLogLoading] = useState(false);
  const [expandedPrompt, setExpandedPrompt] = useState(null);

  useEffect(() => {
    loadTriggerWords();
    loadRemixPrompt();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('snapshot_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadRemixPrompt = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/remix-prompt`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRemixPrompt(data.remix_base_prompt || '');
      }
    } catch (e) {
      console.error('Error loading remix prompt:', e);
    }
  };

  const saveRemixPrompt = async () => {
    try {
      setSavingRemix(true);
      const res = await fetch(`${API_URL}/api/admin/remix-prompt`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ remix_base_prompt: remixPrompt })
      });
      if (!res.ok) throw new Error('Failed to save');
      setMessage({ type: 'success', text: 'Remix-Prompt gespeichert!' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Fehler beim Speichern des Remix-Prompts' });
    } finally {
      setSavingRemix(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const loadRemixLog = async () => {
    try {
      setRemixLogLoading(true);
      const res = await fetch(`${API_URL}/api/admin/remix-log`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRemixLog(data.entries || []);
      }
    } catch (e) {
      console.error('Error loading remix log:', e);
    } finally {
      setRemixLogLoading(false);
    }
  };

  const toggleRemixLog = () => {
    const next = !remixLogOpen;
    setRemixLogOpen(next);
    if (next && remixLog.length === 0) loadRemixLog();
  };

  const getProviderBadge = (provider) => {
    if (!provider) return null;
    const p = provider.toLowerCase();
    if (p.includes('gemini') || p.includes('nano_banana')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-green-500/20 text-green-400 border border-green-500/30">Gemini</span>;
    }
    if (p.includes('grok')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">Grok</span>;
    }
    if (p.includes('astria')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">Astria</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-bold rounded bg-white/10 text-white/60 border border-white/20">{provider}</span>;
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') return <span className="px-2 py-0.5 text-xs rounded bg-green-500/10 text-green-400">completed</span>;
    if (status === 'failed') return <span className="px-2 py-0.5 text-xs rounded bg-red-500/10 text-red-400">failed</span>;
    return <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/10 text-yellow-400">{status}</span>;
  };

  const loadTriggerWords = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/trigger-words`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setTriggerWords(data.trigger_words || []);
      setPromptKeywords(data.prompt_keywords || []);
      setUpdatedAt(data.updated_at);
      setUpdatedBy(data.updated_by);
    } catch (error) {
      console.error('Error loading trigger words:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Trigger Words' });
    } finally {
      setLoading(false);
    }
  };

  const saveTriggerWords = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/admin/trigger-words`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          trigger_words: triggerWords,
          prompt_keywords: promptKeywords
        })
      });
      if (!res.ok) throw new Error('Failed to save');
      const data = await res.json();
      setTriggerWords(data.trigger_words);
      setPromptKeywords(data.prompt_keywords);
      setUpdatedAt(data.updated_at);
      setUpdatedBy(data.updated_by);
      setMessage({ type: 'success', text: 'Gespeichert!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving:', error);
      setMessage({ type: 'error', text: 'Fehler beim Speichern' });
    } finally {
      setSaving(false);
    }
  };

  const addTriggerWord = () => {
    const word = newWord.trim().toLowerCase();
    if (word && !triggerWords.includes(word)) {
      setTriggerWords([...triggerWords, word]);
      setNewWord('');
    }
  };

  const removeTriggerWord = (index) => {
    setTriggerWords(triggerWords.filter((_, i) => i !== index));
  };

  const addPromptKeyword = () => {
    const keyword = newKeyword.trim();
    if (keyword && !promptKeywords.includes(keyword)) {
      setPromptKeywords([...promptKeywords, keyword]);
      setNewKeyword('');
    }
  };

  const removePromptKeyword = (index) => {
    setPromptKeywords(promptKeywords.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e, addFn) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFn();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="w-7 h-7 text-[#10B981]" />
            Trigger Words & Prompt Keywords
          </h2>
          <p className="text-white/50 text-sm mt-1">
            NSFW-Trigger steuern Grok-Routing. Prompt Keywords werden an jede Generierung angehangen.
          </p>
          {updatedAt && (
            <p className="text-white/30 text-xs mt-1">
              Zuletzt aktualisiert: {new Date(updatedAt).toLocaleString('de-DE')}
              {updatedBy && ` von ${updatedBy}`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadTriggerWords}
            className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-xl transition-all flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reload
          </button>
          <button
            onClick={saveTriggerWords}
            disabled={saving}
            className="px-6 py-2 bg-[#10B981] hover:bg-[#0d9668] text-black font-semibold rounded-xl transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Status Message */}
      {message && (
        <div className={`p-4 rounded-xl border ${
          message.type === 'success'
            ? 'bg-green-500/10 border-green-500/30 text-green-400'
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {message.text}
        </div>
      )}

      {/* NSFW Trigger Words Section */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-2">
          <Shield className="w-5 h-5 text-red-400" />
          NSFW Trigger Words
          <span className="text-sm font-normal text-white/40 ml-2">({triggerWords.length} Begriffe)</span>
        </h3>
        <p className="text-white/40 text-sm mb-4">
          Bei diesen Begriffen wird Gemini als Fallback NICHT verwendet (nur Grok).
          Prompts mit diesen Worten werden direkt und ausschliesslich durch Grok verarbeitet.
        </p>

        {/* Add new word */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, addTriggerWord)}
            placeholder="Neues Trigger Word eingeben..."
            className="flex-1 px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]/50"
          />
          <button
            onClick={addTriggerWord}
            disabled={!newWord.trim()}
            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-xl transition-all flex items-center gap-1 disabled:opacity-30"
          >
            <Plus className="w-4 h-4" />
            Hinzufuegen
          </button>
        </div>

        {/* Word Tags */}
        <div className="flex flex-wrap gap-2">
          {triggerWords.map((word, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/20 text-red-300 text-sm rounded-lg group hover:border-red-500/40 transition-all"
            >
              {word}
              <button
                onClick={() => removeTriggerWord(index)}
                className="opacity-50 hover:opacity-100 transition-opacity"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {triggerWords.length === 0 && (
            <p className="text-white/30 text-sm italic">Keine Trigger Words konfiguriert. Standard-Liste wird verwendet.</p>
          )}
        </div>
      </div>

      {/* Prompt Keywords Section */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-2">
          <MessageSquarePlus className="w-5 h-5 text-[#10B981]" />
          Prompt Keywords
          <span className="text-sm font-normal text-white/40 ml-2">({promptKeywords.length} Keywords)</span>
        </h3>
        <p className="text-white/40 text-sm mb-4">
          Diese Keywords werden automatisch an das Ende jedes Generierungs-Prompts angehangen.
          Beispiel: "professional lighting, high resolution"
        </p>

        {/* Add new keyword */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, addPromptKeyword)}
            placeholder="Neues Prompt Keyword eingeben..."
            className="flex-1 px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]/50"
          />
          <button
            onClick={addPromptKeyword}
            disabled={!newKeyword.trim()}
            className="px-4 py-2 bg-[#10B981]/20 hover:bg-[#10B981]/30 text-[#10B981] border border-[#10B981]/30 rounded-xl transition-all flex items-center gap-1 disabled:opacity-30"
          >
            <Plus className="w-4 h-4" />
            Hinzufuegen
          </button>
        </div>

        {/* Keyword Tags */}
        <div className="flex flex-wrap gap-2">
          {promptKeywords.map((keyword, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-sm rounded-lg group hover:border-[#10B981]/40 transition-all"
            >
              {keyword}
              <button
                onClick={() => removePromptKeyword(index)}
                className="opacity-50 hover:opacity-100 transition-opacity"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {promptKeywords.length === 0 && (
            <p className="text-white/30 text-sm italic">Keine Prompt Keywords konfiguriert. Prompts werden ohne Zusatz gesendet.</p>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-5">
        <h4 className="text-blue-400 font-semibold mb-2">Wie funktioniert es?</h4>
        <ul className="text-white/50 text-sm space-y-1.5">
          <li><strong className="text-white/70">Routing:</strong> Grok (xAI) ist der primaere Provider. Gemini ist nur Fallback bei Grok-Fehler.</li>
          <li><strong className="text-white/70">NSFW Trigger:</strong> Wenn ein Prompt ein Trigger Word enthaelt, wird NUR Grok verwendet (kein Gemini-Fallback).</li>
          <li><strong className="text-white/70">Prompt Keywords:</strong> Werden automatisch ans Ende jedes Prompts angehangen (Grok + Gemini).</li>
          <li><strong className="text-white/70">Cache:</strong> Aenderungen werden nach max. 5 Minuten wirksam (Backend-Cache).</li>
        </ul>
      </div>

      {/* Remix Base Prompt */}
      <div className="bg-white/5 border border-purple-500/30 rounded-xl p-4">
        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
          <MessageSquarePlus className="w-5 h-5 text-purple-400" />
          Remix Standard-Prompt (Editing Rules)
        </h3>
        <p className="text-white/40 text-xs mb-3">
          Dieser Text wird jedem Remix vorangestellt. Er sorgt dafür, dass Gesicht, Pose und Hintergrund erhalten bleiben. Der User-Wunsch wird automatisch als "REQUESTED CHANGE:" angehängt.
        </p>
        <textarea
          value={remixPrompt}
          onChange={(e) => setRemixPrompt(e.target.value)}
          className="w-full bg-black/30 border border-white/20 rounded-lg p-3 text-white text-sm font-mono focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all resize-y"
          rows={8}
          placeholder="Professional fashion photography..."
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-white/30 text-xs">{remixPrompt.length} Zeichen</span>
          <button
            onClick={saveRemixPrompt}
            disabled={savingRemix}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-bold rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {savingRemix ? 'Speichert...' : 'Remix-Prompt speichern'}
          </button>
        </div>
      </div>

      {/* Generation Log Section */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <button
          onClick={toggleRemixLog}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-all"
        >
          <div className="flex items-center gap-3">
            <ScrollText className="w-5 h-5 text-orange-400" />
            <span className="text-lg font-semibold text-white">Generation Log</span>
            <span className="text-sm text-white/40">Letzte 30 Generierungen (Remix + Astria)</span>
          </div>
          {remixLogOpen ? <ChevronDown className="w-5 h-5 text-white/40" /> : <ChevronRight className="w-5 h-5 text-white/40" />}
        </button>

        {remixLogOpen && (
          <div className="px-6 pb-6 space-y-3">
            <div className="flex justify-end">
              <button
                onClick={loadRemixLog}
                disabled={remixLogLoading}
                className="px-3 py-1 text-xs bg-white/5 hover:bg-white/10 text-white/60 border border-white/10 rounded-lg flex items-center gap-1"
              >
                <RotateCcw className={`w-3 h-3 ${remixLogLoading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </button>
            </div>

            {remixLogLoading && remixLog.length === 0 ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-400"></div>
              </div>
            ) : remixLog.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-6">Keine Eintraege gefunden.</p>
            ) : (
              <div className="space-y-2">
                {remixLog.map((entry, idx) => (
                  <div key={idx} className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-white/40 text-xs font-mono">
                        {entry.created_at ? new Date(entry.created_at).toLocaleString('de-DE') : '—'}
                      </span>
                      {getProviderBadge(entry.provider_used)}
                      {getStatusBadge(entry.status)}
                      {entry.generation_type === 'remix' && (
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">Remix</span>
                      )}
                      {entry.generation_type === 'standard' && (
                        <span className="px-2 py-0.5 text-xs rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">Standard</span>
                      )}
                      {entry.nsfw_trigger && (
                        <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/30">NSFW</span>
                      )}
                      {entry.num_images > 1 && (
                        <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/40">{entry.num_images} Bilder</span>
                      )}
                      {entry.cost_snaps > 0 && (
                        <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/10 text-yellow-400">{entry.cost_snaps} SNAPs</span>
                      )}
                      <span className="text-white/20 text-xs ml-auto">User: {entry.user_id}</span>
                    </div>
                    <div
                      className="cursor-pointer group"
                      onClick={() => setExpandedPrompt(expandedPrompt === idx ? null : idx)}
                    >
                      <p className={`text-white/70 text-sm font-mono bg-black/30 rounded-lg px-3 py-2 ${expandedPrompt === idx ? '' : 'line-clamp-2'}`}>
                        {entry.safe_prompt || '(kein Prompt)'}
                      </p>
                      {entry.safe_prompt && entry.safe_prompt.length > 120 && expandedPrompt !== idx && (
                        <span className="text-white/30 text-xs">Klicken zum Aufklappen...</span>
                      )}
                    </div>
                    {entry.image_urls && entry.image_urls.length > 0 && (
                      <div className="flex gap-2 mt-1">
                        {entry.image_urls.slice(0, 3).map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt="result" className="w-16 h-16 object-cover rounded-lg border border-white/10 hover:border-white/30 transition-all" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
